import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner/LoadingSpinner';
import { Clock, AlertTriangle, CheckCircle, TrendingUp } from 'lucide-react';
import {
  useGetConsumptionLogsSummaryQuery,
  useGetConsumptionLogsQuery
} from '@/services/api/services/subscriptionsApi';
import { formatDate } from '@/utils/helpers';

const UsageDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const pageLimit = 100;

  // API queries
  const {
    data: summaryData,
    error: summaryError,
    isLoading: summaryLoading
  } = useGetConsumptionLogsSummaryQuery(id!, { skip: !id });

  const {
    data: logsData,
    error: logsError,
    isLoading: logsLoading
  } = useGetConsumptionLogsQuery(
    {
      subscriptionId: id!,
      page: currentPage,
      limit: pageLimit
    },
    { skip: !id }
  );

  const isLoading = summaryLoading || logsLoading;
  const error = summaryError || logsError;

  const formatTimestamp = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getStatusColor = (statusCode: number): string => {
    if (statusCode >= 200 && statusCode < 300) return 'text-green-600';
    if (statusCode >= 400 && statusCode < 500) return 'text-yellow-600';
    if (statusCode >= 500) return 'text-red-600';
    return 'text-gray-600';
  };

  const getStatusIcon = (statusCode: number) => {
    if (statusCode >= 200 && statusCode < 300) return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (statusCode >= 400 && statusCode < 500) return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
    if (statusCode >= 500) return <AlertTriangle className="w-4 h-4 text-red-600" />;
    return <Clock className="w-4 h-4 text-gray-600" />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" text="Loading usage details..." />
      </div>
    );
  }

  if (error || !summaryData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error</h2>
          <p className="text-gray-600">Failed to load usage details</p>
          <Button
            onClick={() => navigate('/my-account/subscriptions')}
            className="mt-4"
          >
            Back to Subscriptions
          </Button>
        </div>
      </div>
    );
  }

  const { summary, logs = [], pagination } = logsData || { logs: [], pagination: null };
  const actualSummary = summary || summaryData;

  // Prepare tool usage breakdown data
  const toolUsageData = Object.entries(actualSummary.tool_usage_breakdown || {}).map(([tool, count]) => ({
    tool,
    count,
    percentage: actualSummary.total_tools_used > 0 ? (count / actualSummary.total_tools_used * 100).toFixed(1) : '0'
  }));

  // Prepare status code breakdown data
  const statusCodeData = Object.entries(actualSummary.status_code_breakdown || {}).map(([code, count]) => ({
    code: parseInt(code),
    count,
    percentage: actualSummary.total_requests > 0 ? (count / actualSummary.total_requests * 100).toFixed(1) : '0'
  }));

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="space-y-2">
        <h1 className="text-4xl font-bold text-gray-900">Usage Details</h1>
        <p className="text-lg text-gray-600">{actualSummary.service_name}</p>
      </div>

      {/* Billing Period Info */}
      <Card className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">Billing Period</h3>
            <p className="text-sm text-gray-600">
              {formatDate(actualSummary.billing_period_start)} - {formatDate(actualSummary.billing_period_end)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Error Rate</p>
            <p className={`text-lg font-semibold ${actualSummary.error_rate > 5 ? 'text-red-600' : 'text-green-600'}`}>
              {actualSummary.error_rate.toFixed(2)}%
            </p>
          </div>
        </div>
      </Card>

      {/* Usage Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <TrendingUp className="w-8 h-8 text-blue-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Total Requests</p>
              <p className="text-2xl font-semibold text-gray-900">{actualSummary.total_requests.toLocaleString()}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <CheckCircle className="w-8 h-8 text-green-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Tools Used</p>
              <p className="text-2xl font-semibold text-gray-900">{actualSummary.total_tools_used.toLocaleString()}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <Clock className="w-8 h-8 text-purple-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Response Time</p>
              <p className="text-2xl font-semibold text-gray-900">{actualSummary.avg_response_time_ms}ms</p>
            </div>
          </div>
        </Card>

        <Card className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertTriangle className={`w-8 h-8 mr-3 ${actualSummary.error_rate > 5 ? 'text-red-600' : 'text-green-600'}`} />
            <div>
              <p className="text-sm font-medium text-gray-600">Error Rate</p>
              <p className={`text-2xl font-semibold ${actualSummary.error_rate > 5 ? 'text-red-600' : 'text-green-600'}`}>
                {actualSummary.error_rate.toFixed(2)}%
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tool Usage Breakdown */}
      {toolUsageData.length > 0 && (
        <Card className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Tool Usage Breakdown</h3>
          <div className="space-y-3">
            {toolUsageData.map(({ tool, count }) => (
              <div key={tool} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">{tool}</code>
                  <span className="text-sm text-gray-600">{count} calls</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Status Code Breakdown */}
      {statusCodeData.length > 0 && (
        <Card className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Status Code Breakdown</h3>
          <div className="space-y-3">
            {statusCodeData.map(({ code, count, percentage }) => (
              <div key={code} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className={`px-2 py-1 rounded text-sm font-mono ${getStatusColor(code)} bg-gray-100`}>
                    {code}
                  </span>
                  <span className="text-sm text-gray-600">{count} requests</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        code >= 200 && code < 300 ? 'bg-green-600' :
                        code >= 400 && code < 500 ? 'bg-yellow-600' :
                        code >= 500 ? 'bg-red-600' : 'bg-gray-600'
                      }`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-600 w-12 text-right">{percentage}%</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Consumption Log */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Consumption Log</h3>

        <Card className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    AI Query
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tool
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Response Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(entry.status_code)}
                        <span className={`text-sm font-mono ${getStatusColor(entry.status_code)}`}>
                          {entry.status_code}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-md">
                      <div className="truncate" title={entry.ai_query}>
                        "{entry.ai_query}"
                      </div>
                      {entry.error_message && (
                        <div className="text-xs text-red-600 mt-1">
                          Error: {entry.error_message}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-black">
                      <code className="px-2 py-1 bg-gray-100 rounded">{entry.tool_name}</code>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {entry.response_time_ms}ms
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatTimestamp(entry.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Pagination */}
        {pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!pagination || currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              ← Previous
            </Button>

            <span className="text-sm text-gray-600">
              Page {currentPage} of {pagination.total_pages} ({pagination.total} total)
            </span>

            <Button
              variant="outline"
              size="sm"
              disabled={!pagination || !pagination.has_next}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next →
            </Button>
          </div>
        )}

        {logs.length === 0 && !logsLoading && (
          <div className="text-center py-8">
            <p className="text-gray-500">No consumption logs found for this subscription.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsageDetails;
